import { Formik, Form, Field, FormikHelpers, FieldProps } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { useUserContext } from '../../hooks/contexts/user.context';
import { DDICountry, getDDIList } from '../../services/getDDI';
import styles from './waitlist.module.css';

export interface WaitlistFormData {
  fullName : string;
  email : string;
  ddi : string;
  countryCode : string;
  whatsapp : string;
}

interface WaitlistFormProps {
  onSubmit : (values : WaitlistFormData) => Promise<void>;
  isSubmitting : boolean;
  error ?: string | null;
}

const validationSchema = Yup.object({
  fullName : Yup.string()
    .required('Nome completo é obrigatório')
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .trim(),
  email : Yup.string().required('E-mail é obrigatório').email('E-mail inválido').trim(),
  whatsapp : Yup.string().required('WhatsApp é obrigatório').min(4, 'WhatsApp inválido').trim(),
});

export const WaitlistForm = ({ onSubmit, isSubmitting, error } : WaitlistFormProps) => {
  const { translate } = useI18nContext();
  const { user } = useUserContext();
  const [ddiList, setDdiList] = useState<DDICountry[]>([]);
  const [ddiLoading, setDdiLoading] = useState(true);

  const fetchDDI = useCallback(async () => {
    try {
      const list = await getDDIList();
      setDdiList(list);
    } catch {
      setDdiList([]);
    } finally {
      setDdiLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDDI();
  }, [fetchDDI]);

  const initialValues : WaitlistFormData = {
    fullName : user?.name || '',
    email : user?.email || '',
    ddi : '55',
    countryCode : 'BR',
    whatsapp : '',
  };

  const handleSubmit = async (
    values : WaitlistFormData,
    { setSubmitting } : FormikHelpers<WaitlistFormData>
  ) => {
    await onSubmit(values);
    setSubmitting(false);
  };

  return (
    <div className={styles.formContainer}>
      <p className={styles.description}>
        {translate(['RESALE_LISTING', 'WAITLIST', 'DESCRIPTION'])}
      </p>

      <div className={styles.attentionBox}>
        <strong>{translate(['RESALE_LISTING', 'WAITLIST', 'ATTENTION_LABEL'])}</strong>{' '}
        {translate(['RESALE_LISTING', 'WAITLIST', 'ATTENTION_MESSAGE'])}
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ errors, touched, handleChange, handleBlur, setFieldValue }) => (
          <Form className={styles.form}>
            <div className={styles.fieldGroup}>
              <label htmlFor="wl-fullName" className={styles.label}>
                {translate(['RESALE_LISTING', 'WAITLIST', 'NAME_LABEL'])}
              </label>
              <Field
                id="wl-fullName"
                name="fullName"
                type="text"
                placeholder={translate(['RESALE_LISTING', 'WAITLIST', 'NAME_PLACEHOLDER'])}
                className={[
                  styles.input,
                  errors.fullName && touched.fullName ? styles.inputError : '',
                ].join(' ')}
                disabled={isSubmitting}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {errors.fullName && touched.fullName && (
                <span className={styles.errorMessage}>{errors.fullName}</span>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="wl-email" className={styles.label}>
                {translate(['RESALE_LISTING', 'WAITLIST', 'EMAIL_LABEL'])}
              </label>
              <Field
                id="wl-email"
                name="email"
                type="email"
                placeholder={translate(['RESALE_LISTING', 'WAITLIST', 'EMAIL_PLACEHOLDER'])}
                className={[
                  styles.input,
                  errors.email && touched.email ? styles.inputError : '',
                ].join(' ')}
                disabled={isSubmitting}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {errors.email && touched.email && (
                <span className={styles.errorMessage}>{errors.email}</span>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="wl-whatsapp" className={styles.label}>
                {translate(['RESALE_LISTING', 'WAITLIST', 'WHATSAPP_LABEL'])}
              </label>
              <div className={styles.phoneRow}>
                <Field name="ddi">
                  {({ field } : FieldProps) => (
                    <select
                      {...field}
                      className={styles.ddiSelect}
                      disabled={isSubmitting || ddiLoading}
                      onChange={(e) => {
                        const selectedDdi = e.target.value;
                        const country = ddiList.find((c) => c.callingCode === selectedDdi);
                        setFieldValue('ddi', selectedDdi);
                        if (country) setFieldValue('countryCode', country.alpha2Code);
                      }}
                    >
                      {ddiLoading ? (
                        <option value="55">+55 BR</option>
                      ) : (
                        ddiList.map((c) => (
                          <option key={c.id} value={c.callingCode}>
                            +{c.callingCode} {c.alpha2Code}
                          </option>
                        ))
                      )}
                    </select>
                  )}
                </Field>
                <Field name="whatsapp">
                  {({ field } : FieldProps) => (
                    <input
                      {...field}
                      id="wl-whatsapp"
                      type="tel"
                      placeholder="(11) 96123-4567"
                      autoComplete="tel"
                      className={[
                        styles.input,
                        styles.phoneInput,
                        errors.whatsapp && touched.whatsapp ? styles.inputError : '',
                      ].join(' ')}
                      disabled={isSubmitting}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  )}
                </Field>
              </div>
              {errors.whatsapp && touched.whatsapp && (
                <span className={styles.errorMessage}>{errors.whatsapp}</span>
              )}
            </div>

            {error && (
              <div className={styles.generalError}>{error}</div>
            )}

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? translate(['RESALE_LISTING', 'WAITLIST', 'SUBMITTING'])
                : translate(['RESALE_LISTING', 'WAITLIST', 'SUBMIT'])}
            </button>

            <p className={styles.terms}>
              {translate(['RESALE_LISTING', 'WAITLIST', 'TERMS_PREFIX'])}{' '}
              <a
                href="https://www.ingresse.com/termos-de-uso"
                target="_blank"
                rel="noreferrer"
                className={styles.link}
              >
                {translate(['RESALE_LISTING', 'WAITLIST', 'TERMS_LINK'])}
              </a>{' '}
              {translate(['RESALE_LISTING', 'WAITLIST', 'TERMS_AND'])}{' '}
              <a
                href="https://www.ingresse.com/politica-de-privacidade"
                target="_blank"
                rel="noreferrer"
                className={styles.link}
              >
                {translate(['RESALE_LISTING', 'WAITLIST', 'PRIVACY_LINK'])}
              </a>
              {translate(['RESALE_LISTING', 'WAITLIST', 'TERMS_SUFFIX'])}
            </p>
          </Form>
        )}
      </Formik>
    </div>
  );
};
